/**
 * Employees roster — Payroll Increment 1.
 *
 * Mirrors `prototype/payroll/employees.html`: rounded-lg initials avatar,
 * name + designation, code, PAN, masked bank, joined date, monthly gross,
 * app-access chip, status. Rows link to the detail page. Plus the
 * "employees ≠ app logins" info note + cursor pagination.
 *
 * @see prototype/payroll/employees.html
 */
import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight, Info, Plus, Search as SearchIcon, UsersRound } from "lucide-react";
import { Alert, Input, TabsRoot, TabsList, TabsTrigger } from "@flint/ui";
import { paiseToInr, paiseToInrShort, formatDate } from "../../../../lib/format.ts";
import { trpc } from "../../../../lib/trpc.ts";
import { usePermission } from "../../../../lib/permissions.ts";
import { EmployeeAvatar } from "../../../../features/employees/index.ts";
import { WorkspaceShell } from "../../../../features/workspace/index.ts";
import { DataTableCard, ListPagination } from "../../../../features/master-list/index.ts";

type StatusFilter = "all" | "active" | "inactive";

interface EmployeesSearch {
  q?: string | undefined;
  status?: StatusFilter | undefined;
}

export const Route = createFileRoute("/(workspace)/payroll/employees/")({
  component: EmployeesListPage,
  validateSearch: (s: Record<string, unknown>): EmployeesSearch => {
    const out: EmployeesSearch = {};
    if (typeof s.q === "string" && s.q) out.q = s.q;
    if (s.status === "active" || s.status === "inactive" || s.status === "all") {
      out.status = s.status;
    }
    return out;
  },
});

const TABS: Array<{ id: StatusFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "inactive", label: "Inactive" },
];

function EmployeesListPage() {
  const canView = usePermission("employees.view");
  const canManage = usePermission("employees.manage");
  const search = Route.useSearch();
  const navigate = useNavigate();
  const [draftQuery, setDraftQuery] = useState(search.q ?? "");

  const status = search.status ?? "all";
  const q = search.q;

  const PAGE_SIZE = 50;
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  useEffect(() => {
    setCursorStack([]);
  }, [status, q]);

  const counts = trpc.employees.counts.useQuery();
  const list = trpc.employees.list.useQuery(
    {
      limit: PAGE_SIZE,
      status,
      ...(cursor !== undefined ? { cursor } : {}),
      ...(q !== undefined ? { q } : {}),
    },
    { placeholderData: (prev) => prev },
  );

  const rows = list.data?.items ?? [];
  const nextCursor = list.data?.nextCursor;

  // Pagination derivations — page index = cursor-stack depth + 1.
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  // Total for the active tab (no per-status filtered count when searching).
  const totalRows =
    q === undefined && counts.data !== undefined
      ? status === "all"
        ? counts.data.active + counts.data.inactive
        : status === "active"
          ? counts.data.active
          : counts.data.inactive
      : undefined;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    if (nextCursor) setCursorStack((s) => [...s, nextCursor]);
  };

  const stats = computeEmployeeStats(rows);

  function applySearch(next: string) {
    setCursorStack([]);
    void navigate({
      to: "/payroll/employees",
      search: { status, q: next || undefined },
    });
  }

  function setStatus(next: StatusFilter) {
    setCursorStack([]);
    void navigate({
      to: "/payroll/employees",
      search: { status: next, ...(q !== undefined ? { q } : {}) },
    });
  }

  const subline =
    counts.data !== undefined
      ? `${counts.data.active} active · ${paiseToInr(counts.data.monthlyPayrollPaise)} monthly payroll`
      : "Loading…";

  if (!canView) {
    return (
      <WorkspaceShell breadcrumbs={[{ label: "Payroll", to: "/payroll/employees" }, { label: "Employees" }]}>
        <div className="flex flex-col items-center justify-center gap-3 py-24 text-center text-muted">
          <UsersRound size={36} className="opacity-30" />
          <p className="text-sm font-medium">You don't have access to Employees.</p>
          <p className="text-xs">Ask a Founder to grant you the <em>payroll.view</em> permission.</p>
        </div>
      </WorkspaceShell>
    );
  }

  return (
    <WorkspaceShell
      breadcrumbs={[{ label: "Payroll", to: "/payroll/employees" }, { label: "Employees" }]}
    >
      <div className="space-y-4">
        {/* Head */}
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-ink">Employees</h1>
            <p className="mt-0.5 text-sm text-muted">{subline}</p>
          </div>
          {canManage && (
            <Link
              to="/payroll/employees/new"
              className="inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2"
            >
              <Plus size={16} aria-hidden />
              Add employee
            </Link>
          )}
        </div>

        {/* Employees ≠ app logins note */}
        <div className="flex items-start gap-3 rounded-md border border-sky-100 bg-sky-50 p-3 text-sm text-sky-800">
          <Info size={18} className="mt-0.5 shrink-0" aria-hidden />
          <p>
            <strong className="text-ink">Employees are who you pay — separate from app logins.</strong>{" "}
            Most won't have a Flint account. The <em>App access</em> column shows when an employee is
            also linked to a login (managed in Settings ▸ Team & roles).
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <TabsRoot value={status} onValueChange={(v) => setStatus(v as StatusFilter)}>
            <TabsList>
              {TABS.map((t) => (
                <TabsTrigger key={t.id} value={t.id}>
                  {t.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </TabsRoot>
          <div className="relative w-full max-w-xs">
            <SearchIcon
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
              aria-hidden
            />
            <Input
              value={draftQuery}
              placeholder="Search name or code…"
              className="pl-9"
              onChange={(e) => setDraftQuery(e.currentTarget.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") applySearch(draftQuery.trim());
              }}
            />
          </div>
        </div>

        {/* Table */}
        {list.error ? (
          <Alert intent="bad" title="Couldn't load employees" description={list.error.message} />
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-dashed border-line bg-paper p-10 text-center">
            <p className="text-sm font-semibold text-ink">No employees yet</p>
            <p className="mt-1 text-sm text-muted">
              Add your first employee to start building the payroll master.
            </p>
            <Link
              to="/payroll/employees/new"
              className="mt-4 inline-flex h-10 items-center gap-2 rounded-sm bg-ink px-4 text-sm font-semibold text-white hover:bg-ink-2"
            >
              <Plus size={16} aria-hidden />
              Add employee
            </Link>
          </div>
        ) : (
          <DataTableCard
            footer={[
              { label: "Visible", value: rows.length },
              { label: "Active", value: stats.activeCount },
              { label: "With login", value: stats.withLoginCount },
              { label: "Page gross", value: paiseToInrShort(stats.sumGrossPaise.toString()) },
              { label: "Avg gross", value: paiseToInrShort(stats.avgGrossPaise.toString()) },
            ]}
            pagination={
              <ListPagination
                pageNumber={pageNumber}
                showingFrom={showingFrom}
                showingTo={showingTo}
                {...(typeof totalRows === "number" ? { total: totalRows } : {})}
                sortLabel="sorted by name"
                hasPrev={cursorStack.length > 0}
                hasNext={!!nextCursor}
                onPrev={handlePrev}
                onNext={handleNext}
              />
            }
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-raised text-left text-xs uppercase tracking-wider text-muted">
                  <th className="px-4 py-3 font-semibold">Employee</th>
                  <th className="px-4 py-3 font-semibold">Code</th>
                  <th className="px-4 py-3 font-semibold">PAN</th>
                  <th className="px-4 py-3 font-semibold">Bank</th>
                  <th className="px-4 py-3 font-semibold">Joined</th>
                  <th className="px-4 py-3 text-right font-semibold">Monthly gross</th>
                  <th className="px-4 py-3 font-semibold">App access</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {rows.map((e) => (
                  <tr
                    key={e.id}
                    className="cursor-pointer border-b border-line last:border-0 hover:bg-bg"
                    onClick={() =>
                      navigate({ to: "/payroll/employees/$employeeId", params: { employeeId: e.id } })
                    }
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <EmployeeAvatar name={e.name} photoUrl={e.photoUrl} size="md" />
                        <div className="min-w-0">
                          <div className="font-semibold text-ink">{e.name}</div>
                          <div className="text-xs text-muted">{e.designation ?? "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 font-mono text-muted">{e.employeeCode ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-muted">{e.pan ?? "—"}</td>
                    <td className="px-4 py-3 font-mono text-muted">
                      {e.bankName || e.bankAccountLast4
                        ? `${e.bankName ?? "Bank"} ···${e.bankAccountLast4 ?? "····"}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 font-mono text-muted">{formatDate(e.dateOfJoining)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-ink">
                      {paiseToInr(e.monthlyGrossPaise)}
                    </td>
                    <td className="px-4 py-3">
                      {e.membershipId ? (
                        <span className="inline-flex items-center rounded-full bg-sky-50 px-2 py-1 text-xs font-semibold text-sky-700">
                          Linked login
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-bg px-2 py-1 text-xs font-semibold text-muted">
                          No login
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusChip status={e.status} />
                    </td>
                    <td className="px-4 py-3 text-right text-muted">
                      <ChevronRight size={16} aria-hidden />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </DataTableCard>
        )}
      </div>
    </WorkspaceShell>
  );
}

/** Visible-set aggregates for the stats footer. */
function computeEmployeeStats(
  rows: Array<{ status: "active" | "inactive"; membershipId: string | null; monthlyGrossPaise: string }>,
): {
  activeCount: number;
  withLoginCount: number;
  sumGrossPaise: bigint;
  avgGrossPaise: bigint;
} {
  if (rows.length === 0) {
    return { activeCount: 0, withLoginCount: 0, sumGrossPaise: 0n, avgGrossPaise: 0n };
  }
  let activeCount = 0;
  let withLoginCount = 0;
  let sum = 0n;
  for (const r of rows) {
    if (r.status === "active") activeCount += 1;
    if (r.membershipId) withLoginCount += 1;
    sum += BigInt(r.monthlyGrossPaise);
  }
  return {
    activeCount,
    withLoginCount,
    sumGrossPaise: sum,
    avgGrossPaise: sum / BigInt(rows.length),
  };
}

function StatusChip({ status }: { status: "active" | "inactive" }) {
  return status === "active" ? (
    <span className="inline-flex items-center rounded-full bg-ok-50 px-2 py-1 text-xs font-semibold text-ok">
      Active
    </span>
  ) : (
    <span className="inline-flex items-center rounded-full bg-bg px-2 py-1 text-xs font-semibold text-muted">
      Inactive
    </span>
  );
}
